import React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import { LineChart } from '@mui/x-charts/LineChart';
import { InventorySyncOverview } from '../../../../models/inventorySync';
import { OVERVIEW_HINTS } from '../../inventorySyncHints';
import { BreakdownRow, breakdownRows, overviewTotals, rollupSeries, shortProcessName } from '../../inventorySyncOverview';
import { HintLabel, formatNumber, formatPercent } from './shared';

/** Окно длиннее — график по 10-минутным суммам, иначе 1440 точек не читаются */
const ROLLUP_AFTER_MINUTES = 120;
const ROLLUP_BUCKET_MINUTES = 10;

function formatWaitMs(ms: number | null | undefined): string {
    if (ms == null) {
        return '—';
    }
    return ms < 1000 ? `${Math.round(ms)} мс` : `${(ms / 1000).toFixed(1)} с`;
}

function formatChartTime(value: Date, withDate: boolean): string {
    return value.toLocaleString('ru-RU', withDate
        ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
        : { hour: '2-digit', minute: '2-digit' });
}

function SummaryCard(props: { label: string; hint: string; value: string; sub?: string }) {
    const { label, hint, value, sub } = props;
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', height: '100%' }}>
            <HintLabel label={label} hint={hint} level="body-sm" />
            <Typography level="h4" sx={{ mt: 0.75 }}>{value}</Typography>
            {sub && <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>{sub}</Typography>}
        </Sheet>
    );
}

function BreakdownTable(props: {
    title: string;
    hint: string;
    rows: BreakdownRow[];
    valueLabel?: string;
    formatKey?: (key: string) => string;
    /** Дополнительная колонка (например 429 по процессу) */
    extra?: { label: string; values: Record<string, number> | null | undefined };
}) {
    const { title, hint, valueLabel = 'Попыток', formatKey = k => k, extra } = props;
    const rows = [
        ...props.rows,
        ...Object.keys(extra?.values ?? {})
            .filter(key => !props.rows.some(row => row.key === key))
            .map(key => ({ key, value: 0, percent: 0 })),
    ];
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', height: '100%', overflow: 'auto' }}>
            <Box sx={{ mb: 1 }}>
                <HintLabel label={title} hint={hint} level="body-sm" />
            </Box>
            {rows.length === 0 ? (
                <Typography level="body-sm" color="neutral">Нет данных за окно</Typography>
            ) : (
                <Table size="sm" borderAxis="bothBetween" sx={{ '--TableCell-headFontWeight': 600 }}>
                    <thead>
                        <tr>
                            <th />
                            <th style={{ textAlign: 'right' }}>{valueLabel}</th>
                            <th style={{ textAlign: 'right' }}>Доля</th>
                            {extra && <th style={{ textAlign: 'right' }}>{extra.label}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.key}>
                                <td>{formatKey(row.key)}</td>
                                <td style={{ textAlign: 'right' }}>{formatNumber(row.value)}</td>
                                <td style={{ textAlign: 'right' }}>{formatPercent(row.percent)}</td>
                                {extra && <td style={{ textAlign: 'right' }}>{formatNumber(extra.values?.[row.key] ?? 0)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Sheet>
    );
}

export function OverviewPanel(props: { overview: InventorySyncOverview }) {
    const { overview } = props;
    const totals = React.useMemo(() => overviewTotals(overview), [overview]);
    const rolledUp = overview.minutes > ROLLUP_AFTER_MINUTES;
    const points = React.useMemo(
        () => rollupSeries(overview.series, rolledUp ? ROLLUP_BUCKET_MINUTES : 1),
        [overview.series, rolledUp]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <SummaryCard
                    label="Ёмкость"
                    hint={OVERVIEW_HINTS.capacity}
                    value={`${formatNumber(overview.capacityPerMinute)} стр/мин`}
                    sub={`${formatNumber(overview.activeProxies)} прокси × ${formatNumber(overview.requestsPerMinutePerProxy)}/мин`}
                />
                <SummaryCard
                    label="Утилизация"
                    hint={OVERVIEW_HINTS.utilization}
                    value={formatPercent(overview.utilizationPercent)}
                    sub={`${formatNumber(totals.attempts)} попыток за окно`}
                />
                <SummaryCard
                    label="Ожидание паузы"
                    hint={OVERVIEW_HINTS.pauseWait}
                    value={`${formatWaitMs(overview.pauseWaitAvgMs)} / ${formatWaitMs(overview.pauseWaitP95Ms)}`}
                    sub="среднее / p95"
                />
                <SummaryCard
                    label="Очередь"
                    hint={OVERVIEW_HINTS.queueDepth}
                    value={formatNumber(totals.queueDepth)}
                    sub="запросов ждут сейчас"
                />
                <SummaryCard
                    label="HTTP 429"
                    hint={OVERVIEW_HINTS.rateLimited}
                    value={formatNumber(totals.rateLimited)}
                    sub={`${formatPercent(totals.rateLimitedPercent)} от попыток`}
                />
                <SummaryCard
                    label="Отказы admission"
                    hint={OVERVIEW_HINTS.admissionRejected}
                    value={formatNumber(totals.admissionRejected)}
                    sub="синков сразу в fallback"
                />
                <SummaryCard
                    label="Fallback"
                    hint={OVERVIEW_HINTS.fallback}
                    value={formatPercent(totals.fallbackPercent)}
                    sub={`${formatNumber(totals.fallbackBatches)} из ${formatNumber(totals.fallbackBatches + totals.publicLinkBatches)} страниц`}
                />
            </Box>

            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                <HintLabel
                    label={rolledUp ? 'Запросы и 429 (по 10 минут)' : 'Запросы и 429 по минутам'}
                    hint={OVERVIEW_HINTS.chart}
                    level="body-sm"
                />
                {points.length > 0 ? (
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <LineChart
                            height={280}
                            dataset={points as unknown as Array<Record<string, number | Date>>}
                            xAxis={[{
                                dataKey: 'x',
                                scaleType: 'time',
                                valueFormatter: (v: Date) => formatChartTime(v, rolledUp),
                            }]}
                            series={[
                                { dataKey: 'attempts', label: 'HTTP-попытки', color: '#4c6ef5', showMark: false },
                                { dataKey: 'rateLimited', label: '429', color: '#e03131', showMark: false },
                                { dataKey: 'admissionRejected', label: 'Отказы admission', color: '#f08c00', showMark: false },
                            ]}
                            margin={{ left: 50, right: 20, top: 40, bottom: 30 }}
                        />
                    </Box>
                ) : (
                    <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>Нет данных за окно</Typography>
                )}
            </Sheet>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                <BreakdownTable
                    title="По процессам"
                    hint={OVERVIEW_HINTS.byProcess}
                    rows={breakdownRows(overview.attemptsByProcess)}
                    formatKey={shortProcessName}
                    extra={{ label: '429', values: overview.rateLimitedByProcess }}
                />
                <BreakdownTable
                    title="По приоритету"
                    hint={OVERVIEW_HINTS.byPriority}
                    rows={breakdownRows(overview.attemptsByPriority)}
                    extra={{ label: 'Отказы admission', values: overview.admissionRejectedByPriority }}
                />
                <BreakdownTable
                    title="По классу эндпоинта"
                    hint={OVERVIEW_HINTS.byEndpointClass}
                    rows={breakdownRows(overview.attemptsByEndpointClass)}
                />
                <BreakdownTable
                    title="Страницы по источникам"
                    hint={OVERVIEW_HINTS.bySource}
                    rows={breakdownRows(overview.batchesBySource)}
                    valueLabel="Страниц"
                />
                <BreakdownTable
                    title="По прокси"
                    hint={OVERVIEW_HINTS.byProxy}
                    rows={breakdownRows(overview.requestsByProxy)}
                    formatKey={key => `#${key}`}
                />
            </Box>
        </Box>
    );
}

export default OverviewPanel;
